'use strict'

const ACTIVITY_SERIES = [
  { key: 'loads', label: 'loads', color: '#4a9eff' },
  { key: 'submits', label: 'submissions', color: '#6cc644' }
]

const PEOPLE_SERIES = [
  { key: 'logins', label: 'logins', color: '#b48eff' },
  { key: 'users', label: 'users', color: '#e6b450' }
]

const WIDTH = 760
const HEIGHT = 210
const PAD = { l: 52, r: 28, t: 16, b: 36 }

function buildActivityLineChart (days, formatDate, which) {

  const rows = Array.isArray(days) ? days : []
  const format = typeof formatDate === 'function' ? formatDate : (date) => String(date || '')
  const lines = which === 'people' ? PEOPLE_SERIES : ACTIVITY_SERIES
  const plotW = WIDTH - PAD.l - PAD.r
  const plotH = HEIGHT - PAD.t - PAD.b
  const n = rows.length
  const plotted = rows.flatMap(day => lines.map(item => seriesValue(day, item.key))).filter(value => value != null)
  const max = plotted.length === 0 ? 1 : Math.max(1, ...plotted)

  const xAt = (index) => n <= 1 ? PAD.l + plotW / 2 : PAD.l + (index / (n - 1)) * plotW
  const yAt = (value) => PAD.t + plotH * (1 - value / max)
  const labelStep = Math.max(1, Math.ceil(n / 16))
  const xs = rows.map((_, index) => coord(xAt(index)))
  const plotRight = coord(PAD.l + plotW)

  const series = lines.map(item => {

    const points = rows.map((day, index) => {

      const value = seriesValue(day, item.key)

      return {
        index,
        x: xs[index],
        y: value == null ? null : coord(yAt(value)),
        value
      }
    })

    return {
      key: item.key,
      label: item.label,
      color: item.color,
      points,
      dots: points.filter(point => point.value != null),
      segments: lineSegments(points)
    }
  })

  const ticks = unique([max, Math.round(max / 2), 0]).map(value => ({
    value,
    y: coord(yAt(value)),
    label: value.toLocaleString('en-US')
  }))

  const columns = rows.map((day, index) => {

    const left = index === 0 ? PAD.l : coord((xs[index - 1] + xs[index]) / 2)
    const right = index === n - 1 ? plotRight : coord((xs[index] + xs[index + 1]) / 2)

    return {
      index,
      x: xs[index],
      hitX: left,
      hitW: Math.max(right - left, 1),
      hitY: PAD.t,
      hitH: HEIGHT - PAD.t,
      date: day.date,
      label: format(day.date),
      showLabel: index % labelStep === 0 || index === n - 1,
      loads: count(day.loads),
      submits: count(day.submits),
      logins: count(day.logins),
      users: seriesValue(day, 'users')
    }
  })

  return {
    width: WIDTH,
    height: HEIGHT,
    pad: PAD,
    plotH,
    max,
    series,
    ticks,
    columns,
    dotR: n > 40 ? 2.4 : 3.2
  }
}

function pageActivityDays (timeRows, userRows, trackedFrom) {

  const byDate = {}

  for (const row of timeRows || []) {

    const date = dateKey(row.date)

    if (!date) continue
    if (!byDate[date]) byDate[date] = { loads: 0, submits: 0, logins: 0, total: 0 }

    const amount = count(row.count)

    if (row.event_type === 'login') byDate[date].logins += amount
    else if (row.event_type === 'page_component_submit') byDate[date].submits += amount
    else byDate[date].loads += amount

    byDate[date].total += amount
  }

  const usersByDate = {}

  for (const row of userRows || []) {

    const date = dateKey(row.date)

    if (!date) continue

    usersByDate[date] = count(row.count)
    if (!byDate[date]) byDate[date] = { loads: 0, submits: 0, logins: 0, total: 0 }
  }

  const first = dateKey(trackedFrom)

  return Object.keys(byDate).sort().map(date => {

    let users = null

    if (Object.prototype.hasOwnProperty.call(usersByDate, date)) users = usersByDate[date]
    else if (first && date >= first) users = 0

    return { date, ...byDate[date], users }
  })
}

function seriesValue (day, key) {

  if (key === 'users') return optionalCount(day && day.users)

  return count(day && day[key])
}

function optionalCount (value) {

  if (value == null || value === '') return null

  return count(value)
}

function count (value) {

  const number = Number(value)

  if (!Number.isFinite(number) || number < 0) return 0

  return number
}

function lineSegments (points) {

  const segments = []
  let current = []

  for (const point of points) {

    if (point.value == null) {

      if (current.length) segments.push(current.map(item => `${item.x},${item.y}`).join(' '))
      current = []
      continue
    }

    current.push(point)
  }

  if (current.length) segments.push(current.map(item => `${item.x},${item.y}`).join(' '))

  return segments
}

function dateKey (value) {

  if (value == null || value === '') return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10)

  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/)

  return match ? match[1] : null
}

function coord (value) {

  return Math.round(value * 10) / 10
}

function unique (values) {

  const seen = new Set()
  const out = []

  for (const value of values) {

    if (seen.has(value)) continue

    seen.add(value)
    out.push(value)
  }

  return out
}

module.exports = {
  buildActivityLineChart,
  pageActivityDays
}
